import Seo, { Breadcrumbs } from "@/components/Seo";

export default function Terms() {
  return (
    <div className="container-bl py-10 max-w-3xl" data-testid="page-terms">
      <Seo title="Terms & Cancellation Policy · B&L Rentals" description="Rental terms, minimum booking lengths, and cancellation & refund policies for B&L Rentals in Oxnard." canonical={typeof window !== "undefined" ? window.location.origin + "/terms" : undefined} />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Terms" }]} />
      <h1 className="text-4xl font-heading font-bold text-white">Terms & Cancellation Policy</h1>
      <p className="mt-3 text-[#AEB9C9]">Effective January 1, 2026. Please review before booking.</p>

      <div className="mt-8 space-y-6 text-[#E5EAF2] leading-relaxed">
        <section>
          <h2 className="text-xl font-heading font-semibold text-white">1. Rental terms</h2>
          <ul className="mt-2 list-disc pl-6 space-y-1 text-sm">
            <li>Renters must be at least 21 years old and hold a valid state-issued driver's license.</li>
            <li>Active personal auto insurance is required for every rental.</li>
            <li>Renters agree not to sublet, race, or use the vehicle for commercial ride-share.</li>
            <li>Smoking and pets (except service animals) are not permitted in any vehicle.</li>
          </ul>
        </section>
        <section>
          <h2 className="text-xl font-heading font-semibold text-white">2. Minimum booking</h2>
          <p className="mt-2 text-sm">Most vehicles have a 1-day minimum. Certain shuttle vans require a 2-day minimum for weekend trips. Each vehicle page shows its specific minimum.</p>
        </section>
        <section>
          <h2 className="text-xl font-heading font-semibold text-white">3. Cancellations & refunds</h2>
          <ul className="mt-2 list-disc pl-6 space-y-1 text-sm">
            <li><b>More than 48 hours before pickup:</b> full refund, no fee.</li>
            <li><b>Inside 48 hours:</b> 50% refund.</li>
            <li><b>No-show or day-of cancellation:</b> non-refundable.</li>
            <li>Refunds are issued to the original payment method within 5 business days.</li>
          </ul>
        </section>
        <section>
          <h2 className="text-xl font-heading font-semibold text-white">4. Damage & incidents</h2>
          <p className="mt-2 text-sm">Renters are financially responsible for damage caused during the rental period, up to the vehicle's stated market value. Insurance claims must be filed within 24 hours of the incident.</p>
        </section>
        <section>
          <h2 className="text-xl font-heading font-semibold text-white">5. Fuel & charging</h2>
          <p className="mt-2 text-sm">Return with the same fuel level (or state of charge) as pickup. Otherwise you'll be charged the fuel or charging amount plus a $20 service fee.</p>
        </section>
        <section id="cleaning-fees">
          <h2 className="text-xl font-heading font-semibold text-white">6. Cleaning & smoking fees</h2>
          <p className="mt-2 text-sm">Every vehicle is professionally detailed between rentals. Renters are expected to return the vehicle in the same condition it was received. If additional cleaning is required beyond a standard reset, one of the tiered fees below will apply. Fees are assessed by our staff after inspection and documented with photos.</p>

          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="font-heading font-semibold text-red-800">No-smoking fee — $250</div>
            <p className="mt-1 text-xs text-red-900 leading-relaxed">
              Charged for any evidence of smoking, vaping, or cannabis use inside the vehicle — including lingering smoke or vape odor, ash, burn marks, or residue on any surface. This fee is in addition to any applicable cleaning fee (typically Deep Cleaning) required to restore the vehicle.
            </p>
          </div>

          <div className="mt-4 space-y-3">
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="font-heading font-semibold text-white">Tier 1 — Light cleaning</div>
                <div className="text-sm font-semibold text-[#E2AB20]">$50</div>
              </div>
              <p className="mt-1 text-xs text-[#AEB9C9] leading-relaxed">
                Excessive dust, sand, or general litter left in the cabin that exceeds a standard reset. Examples: sand from the beach on floor mats, empty cups or food wrappers left behind, minor sticky spots on surfaces, crumbs across seats or floor.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="font-heading font-semibold text-white">Tier 2 — Medium cleaning</div>
                <div className="text-sm font-semibold text-[#E2AB20]">$100</div>
              </div>
              <p className="mt-1 text-xs text-[#AEB9C9] leading-relaxed">
                Notable stains or spills requiring targeted spot-cleaning. Examples: coffee, soda, or juice spilled on one seat or floor mat; sticky residue on multiple surfaces; visible mud tracked into the cabin; strong food odors requiring deodorization; light pet hair (service animals excepted).
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="font-heading font-semibold text-white">Tier 3 — Heavy cleaning</div>
                <div className="text-sm font-semibold text-[#E2AB20]">$150</div>
              </div>
              <p className="mt-1 text-xs text-[#AEB9C9] leading-relaxed">
                Multiple stains or spills across seats and carpet; heavy soiling from mud, sand, or dirt throughout the cabin; food or drink spilled into upholstery seams or crevices; surfaces requiring full-cabin shampoo or upholstery treatment; heavy pet hair throughout.
              </p>
            </div>

            <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4">
              <div className="flex items-center justify-between">
                <div className="font-heading font-semibold text-red-800">Tier 4 — Deep cleaning</div>
                <div className="text-sm font-semibold text-red-700">$300</div>
              </div>
              <p className="mt-1 text-xs text-red-900 leading-relaxed">
                Extreme conditions requiring professional sanitization or extended labor. Examples: bodily fluids (vomit, blood, urine, feces); heavy grease, oil, or chemical spills; cargo spills soaked into carpet or padding; wet outdoor gear left in the cabin resulting in mildew or mold; heavy animal shedding (non-service) throughout the interior; smoke damage requiring full ozone treatment (typically combined with the $250 no-smoking fee).
              </p>
            </div>
          </div>

          <p className="mt-4 text-xs text-[#AEB9C9] leading-relaxed">
            Only one cleaning tier is charged per rental — the tier that best matches the actual condition of the vehicle at return. In cases where multiple issues apply, the higher tier will be used. Photo documentation is shared with the renter before any charge is applied, and disputes may be raised within 48 hours of the charge notice.
          </p>
        </section>
        <section>
          <h2 className="text-xl font-heading font-semibold text-white">7. Contact</h2>
          <p className="mt-2 text-sm">Questions about these terms? Email <a className="link-blue" href="mailto:hello@blrental.com">hello@blrental.com</a> or call <a className="link-blue" href="tel:+18052908431">(805) 290-8431</a>.</p>
        </section>
      </div>
    </div>
  );
}
